import Pusher, { Channel } from 'pusher-js';
import type { ChannelAuthorizationCallback, ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options';
import { showReportFailedToast, showReportSuccessToast } from 'src/components/Toast';
import { apolloClient } from 'src/graphql/client';
import { ExportStatus } from 'src/graphql/_generated';
import { navigateGlobal } from 'src/utils/navigation';
import { getAccessToken } from 'src/utils/tokenService';

// Per-job progress stream over Laravel Reverb (Pusher protocol). One export =
// one private channel = one short-lived subscription. See WS_NOTIFICATIONS_SPA_GUIDE.md
// for protocol details and REVERB_SETUP.md for infra.

interface ExportProgressEvent {
  uxId: string;
  status: string;
  progress: number;
}

interface ExportCompletedEvent {
  uxId: string;
  status: string;
  errorMessage: string | null;
}

let pusherSingleton: Pusher | null = null;
const activeSubscriptions = new Map<string, Channel>();

function reverbHost(): string {
  // BASE_URL is the explicit test origin in dev (`https://risk.test.cydea.app/`)
  // and `/` in prod (root-relative). In prod the SPA is served from the same
  // host as Reverb, so window.location.host is correct; in dev that would
  // resolve to `localhost:3333` which has no Reverb — so prefer BASE_URL's host
  // when it's an absolute URL.
  const baseUrl = process.env.BASE_URL;
  if (baseUrl && baseUrl !== '/' && /^https?:\/\//i.test(baseUrl)) {
    try {
      return new URL(baseUrl).host;
    } catch {
      // fall through
    }
  }
  if (typeof window !== 'undefined' && window.location) {
    return window.location.host;
  }
  return '';
}

function getPusher(): Pusher | null {
  if (pusherSingleton) return pusherSingleton;

  const key = process.env.REVERB_APP_KEY;
  const host = reverbHost();
  if (!key || !host) {
    console.warn('[exportSocket] REVERB_APP_KEY or host missing; live progress disabled');
    return null;
  }

  pusherSingleton = new Pusher(key, {
    wsHost: host,
    wsPort: 443,
    wssPort: 443,
    forceTLS: true,
    // Transport is always named 'ws'; forceTLS upgrades it to wss://.
    // Listing only 'wss' leaves pusher-js with no resolvable transport.
    enabledTransports: ['ws', 'wss'],
    // Required by pusher-js at construction; ignored by Reverb.
    cluster: 'mt1',
    channelAuthorization: {
      // customHandler lets us resolve the JWT fresh per auth POST. The SPA is
      // long-lived; static headers would freeze a token that expires.
      customHandler: (params, callback) => {
        void authoriseChannel(params.socketId, params.channelName, callback);
      },
    },
  });

  pusherSingleton.connection.bind('error', (err: unknown) => {
    console.warn('[exportSocket] connection error', err);
  });

  // Cover transient drops (network blip, backend deploy restart): events that
  // fire during the gap are not buffered, so re-fetch the job list on reconnect.
  let wasConnected = false;
  pusherSingleton.connection.bind('state_change', ({ current }: { current: string }) => {
    if (current === 'connected' && wasConnected) {
      apolloClient.refetchQueries({ include: ['myExportJobs'] }).catch(() => {});
    }
    if (current === 'connected') wasConnected = true;
  });

  return pusherSingleton;
}

async function authoriseChannel(
  socketId: string,
  channelName: string,
  callback: ChannelAuthorizationCallback,
): Promise<void> {
  try {
    const token = await getAccessToken();
    if (!token) {
      callback(new Error('no access token'), null);
      return;
    }
    const res = await fetch(`${process.env.BASE_URL}api/public/broadcasting/auth`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        socket_id: socketId,
        channel_name: channelName,
      }),
    });
    if (!res.ok) {
      console.warn(`[exportSocket] broadcasting/auth ${res.status}`);
      callback(new Error(`broadcasting/auth ${res.status}`), null);
      return;
    }
    callback(null, (await res.json()) as ChannelAuthorizationData);
  } catch (e) {
    console.warn('[exportSocket] auth threw', e);
    callback(e instanceof Error ? e : new Error(String(e)), null);
  }
}

function notifyTerminal(e: ExportCompletedEvent): void {
  const status = e.status.toLowerCase();
  if (status === 'completed') {
    showReportSuccessToast({
      onCtaClick: () => navigateGlobal('/report-centre'),
    });
    return;
  }
  if (status === 'failed' || status === 'canceled') {
    showReportFailedToast(e.errorMessage ? { subtitle: e.errorMessage } : undefined);
  }
}

function writeStatus(uxId: string, status: string, extras: Record<string, unknown> = {}): void {
  const enumStatus = status.toUpperCase() as ExportStatus;
  apolloClient.cache.modify({
    id: apolloClient.cache.identify({ __typename: 'ExportJob', id: uxId }),
    fields: {
      status: () => enumStatus,
      ...Object.fromEntries(Object.entries(extras).map(([k, v]) => [k, () => v])),
    },
  });
}

/**
 * Subscribe to live progress for an in-flight export job. Idempotent — calling
 * with the same uxId returns the existing subscription. Auto-tears down on the
 * terminal `export.completed` event.
 */
export function subscribeToExportJob(uxId: string): () => void {
  if (activeSubscriptions.has(uxId)) {
    return () => unsubscribe(uxId);
  }

  const pusher = getPusher();
  if (!pusher) return () => {};

  const channelName = `private-export.${uxId}`;
  const channel = pusher.subscribe(channelName);
  activeSubscriptions.set(uxId, channel);

  channel.bind('pusher:subscription_error', (err: { status?: number } | unknown) => {
    const status = (err as { status?: number })?.status;
    console.warn(`[exportSocket] subscribe failed for ${channelName}`, status ?? err);
    unsubscribe(uxId);
  });

  channel.bind('export.progress', (e: ExportProgressEvent) => {
    writeStatus(e.uxId, e.status, { progress: e.progress });
  });

  channel.bind('export.completed', (e: ExportCompletedEvent) => {
    writeStatus(e.uxId, e.status, { errorMessage: e.errorMessage });
    // The WS payload omits terminal-state timestamps (completedAt / failedAt /
    // canceledAt) — refetch to pick them up so the Report Centre row renders
    // correctly without waiting for the next poll.
    apolloClient.refetchQueries({ include: ['myExportJobs'] }).catch(() => {});
    notifyTerminal(e);
    unsubscribe(uxId);
  });

  return () => unsubscribe(uxId);
}

function unsubscribe(uxId: string): void {
  if (!activeSubscriptions.has(uxId)) return;
  activeSubscriptions.delete(uxId);
  pusherSingleton?.unsubscribe(`private-export.${uxId}`);
}
